import { useMemo, useState } from 'react';

import {
  buildBerthPlanLayout,
  findMatchingVisitIds,
  getBlockSequence,
  PX_PER_HOUR,
  ZOOM_PX_PER_HOUR,
} from '@/components/blocks/berth-plan/berth-plan.utils';
import { BerthPlanVisit } from '@/components/blocks/berth-plan/types';

const DEFAULT_ZOOM_INDEX = ZOOM_PX_PER_HOUR.indexOf(PX_PER_HOUR);

/** View state of the Berth Plan: search, history fold, zoom and what they derive. */
export const useBerthPlanView = (
  visits: ReadonlyArray<BerthPlanVisit>,
  cutoffMs?: number
) => {
  const [query, setQuery] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);

  const layout = useMemo(
    () => buildBerthPlanLayout(visits, cutoffMs, showHistory),
    [visits, cutoffMs, showHistory]
  );
  const matchIds = useMemo(
    () => findMatchingVisitIds(visits, query),
    [visits, query]
  );
  const matchSequence = useMemo(
    () => (layout && matchIds ? getBlockSequence(layout.blocks, matchIds) : []),
    [layout, matchIds]
  );
  const conflictSequence = useMemo(
    () => (layout ? getBlockSequence(layout.blocks, layout.conflictIds) : []),
    [layout]
  );

  return {
    layout,
    query,
    setQuery,
    matchIds,
    matchSequence,
    conflictSequence,
    showHistory,
    setShowHistory,
    pxPerHour: ZOOM_PX_PER_HOUR.at(zoomIndex) ?? PX_PER_HOUR,
    canZoomIn: zoomIndex < ZOOM_PX_PER_HOUR.length - 1,
    canZoomOut: zoomIndex > 0,
    zoomIn: () => {
      setZoomIndex((index) => Math.min(index + 1, ZOOM_PX_PER_HOUR.length - 1));
    },
    zoomOut: () => {
      setZoomIndex((index) => Math.max(index - 1, 0));
    },
  };
};
